import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { Bot, Instagram, Youtube, Send, Loader2, Trash2, Check, X, Shield, Plus, AlertTriangle } from 'lucide-react';
import { toast } from 'sonner';
import { useAdminLang } from '../../hooks/useAdminLang';

const PLATFORM_ICON = {
  instagram: Instagram,
  tiktok: Send, // no TikTok icon in lucide — send emoji as placeholder
  youtube_shorts: Youtube,
};

/**
 * SocialAIAgentsPanel — Admin section to compose + schedule Social AI posts,
 * with a forbidden-terms auto-moderation config. Actual publishing to
 * Instagram / TikTok / YouTube requires per-platform OAuth (see `publish_hook`
 * in the backend module) — this panel handles the entire lifecycle up to
 * "mark as published" once your integration is live.
 */
export default function SocialAIAgentsPanel() {
  const { at } = useAdminLang();
  const [tab, setTab] = useState('compose');
  const [posts, setPosts] = useState([]);
  const [counts, setCounts] = useState({});
  const [loading, setLoading] = useState(true);
  const [form, setForm] = useState({ title: '', body: '', platforms: [], media_url: '', scheduled_at: '', hashtags: '' });
  const [submitting, setSubmitting] = useState(false);
  const [forbidden, setForbidden] = useState([]);
  const [newTerm, setNewTerm] = useState('');

  const load = async () => {
    setLoading(true);
    try {
      const [p, mc] = await Promise.all([
        axios.get('/api/admin-ext/social-agents/posts'),
        axios.get('/api/admin-ext/social-agents/moderation-config'),
      ]);
      setPosts(p.data?.items || []);
      setCounts(p.data?.counts || {});
      setForbidden(mc.data?.forbidden_terms || []);
    } catch { toast.error(at('saError')); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  const togglePlat = (p) => {
    setForm(f => ({ ...f, platforms: f.platforms.includes(p) ? f.platforms.filter(x => x !== p) : [...f.platforms, p] }));
  };

  const submit = async () => {
    if (!form.title.trim() || !form.body.trim() || form.platforms.length === 0) {
      toast.error(at('saMissing'));
      return;
    }
    setSubmitting(true);
    try {
      const r = await axios.post('/api/admin-ext/social-agents/posts', form);
      if (r.data.status === 'flagged') {
        toast.warning(at('saFlagged'));
      } else {
        toast.success(at('saScheduled'));
      }
      setForm({ title: '', body: '', platforms: [], media_url: '', scheduled_at: '', hashtags: '' });
      await load();
      setTab('list');
    } catch (e) { toast.error(e?.response?.data?.detail || at('saError')); }
    finally { setSubmitting(false); }
  };

  const updateStatus = async (id, status) => {
    try {
      await axios.post(`/api/admin-ext/social-agents/posts/${id}/status`, { status });
      await load();
    } catch { toast.error(at('saError')); }
  };

  const del = async (id) => {
    if (!window.confirm(at('saDeleteConfirm'))) return;
    try {
      await axios.delete(`/api/admin-ext/social-agents/posts/${id}`);
      await load();
    } catch { toast.error(at('saError')); }
  };

  const addTerm = () => {
    if (newTerm.trim() && !forbidden.includes(newTerm.trim())) {
      setForbidden([...forbidden, newTerm.trim()]);
      setNewTerm('');
    }
  };
  const removeTerm = (t) => setForbidden(forbidden.filter(x => x !== t));
  const saveMod = async () => {
    try {
      await axios.post('/api/admin-ext/social-agents/moderation-config', { forbidden_terms: forbidden });
      toast.success(at('saModSaved'));
    } catch { toast.error(at('saError')); }
  };

  return (
    <div className="p-4 rounded-2xl bg-gradient-to-br from-indigo-500/10 to-purple-500/10 border border-indigo-500/30" data-testid="social-ai-agents-panel">
      <div className="flex items-center gap-2 mb-3">
        <Bot className="w-4 h-4 text-indigo-300" />
        <h4 className="font-bold text-indigo-200 text-sm">{at('saTitle')}</h4>
      </div>

      {/* Tabs */}
      <div className="flex gap-1 mb-3 p-0.5 rounded-full bg-black/40 border border-white/10">
        {[
          { id: 'compose', label: at('saTabCompose') },
          { id: 'list', label: `${at('saTabList')} (${posts.length})` },
          { id: 'mod', label: at('saTabMod') },
        ].map(t => (
          <button key={t.id} onClick={() => setTab(t.id)} className={`flex-1 py-1.5 rounded-full text-[11px] font-semibold ${tab === t.id ? 'bg-indigo-500 text-white' : 'text-white/70 hover:text-white'}`} data-testid={`sa-tab-${t.id}`}>
            {t.label}
          </button>
        ))}
      </div>

      {loading ? <Loader2 className="w-4 h-4 animate-spin mx-auto" /> : (
        <>
          {tab === 'compose' && (
            <div className="space-y-2" data-testid="sa-compose">
              <input value={form.title} onChange={e => setForm({ ...form, title: e.target.value })} placeholder={at('saTitleField')} className="w-full bg-black/40 border border-indigo-500/30 rounded-full px-3 py-2 text-sm placeholder-white/40 focus:outline-none focus:border-indigo-400 text-white" data-testid="sa-title" />
              <textarea value={form.body} onChange={e => setForm({ ...form, body: e.target.value })} placeholder={at('saBodyField')} rows={4} className="w-full bg-black/40 border border-indigo-500/30 rounded-2xl p-3 text-sm placeholder-white/40 focus:outline-none focus:border-indigo-400 text-white" data-testid="sa-body" />
              <div className="flex flex-wrap gap-1.5">
                {['instagram', 'tiktok', 'youtube_shorts'].map(p => {
                  const Icon = PLATFORM_ICON[p];
                  const on = form.platforms.includes(p);
                  return (
                    <button key={p} onClick={() => togglePlat(p)} className={`px-3 py-1.5 rounded-full text-xs font-semibold flex items-center gap-1.5 ${on ? 'bg-indigo-500 text-white' : 'bg-white/10 text-white/70'}`} data-testid={`sa-plat-${p}`}>
                      <Icon className="w-3 h-3" /> {p.replace('_', ' ')}
                    </button>
                  );
                })}
              </div>
              <input type="datetime-local" value={form.scheduled_at} onChange={e => setForm({ ...form, scheduled_at: e.target.value })} className="w-full bg-black/40 border border-indigo-500/30 rounded-full px-3 py-2 text-sm text-white focus:outline-none focus:border-indigo-400" data-testid="sa-schedule" />
              <input value={form.hashtags} onChange={e => setForm({ ...form, hashtags: e.target.value })} placeholder={at('saHashtags')} className="w-full bg-black/40 border border-indigo-500/30 rounded-full px-3 py-2 text-sm placeholder-white/40 focus:outline-none focus:border-indigo-400 text-white" data-testid="sa-hashtags" />
              <input value={form.media_url} onChange={e => setForm({ ...form, media_url: e.target.value })} placeholder={at('saMediaUrl')} className="w-full bg-black/40 border border-indigo-500/30 rounded-full px-3 py-2 text-sm placeholder-white/40 focus:outline-none focus:border-indigo-400 text-white" data-testid="sa-media-url" />
              <button onClick={submit} disabled={submitting} className="w-full py-2.5 rounded-full bg-gradient-to-r from-indigo-500 to-purple-600 disabled:opacity-40 text-white font-semibold text-sm flex items-center justify-center gap-2" data-testid="sa-submit">
                {submitting ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}
                {at('saSchedule')}
              </button>
            </div>
          )}

          {tab === 'list' && (
            <div className="space-y-2 max-h-72 overflow-y-auto" data-testid="sa-list">
              {posts.length === 0 ? <div className="text-center py-4 text-[11px] text-white/50">{at('saEmpty')}</div> :
                posts.map(p => (
                  <div key={p.id} className={`p-2.5 rounded-xl border ${p.status === 'flagged' ? 'bg-red-500/10 border-red-500/40' : p.status === 'published' ? 'bg-green-500/10 border-green-500/40' : 'bg-black/40 border-indigo-500/20'}`}>
                    <div className="flex items-center justify-between gap-2">
                      <div className="flex-1 min-w-0">
                        <div className="text-xs font-semibold text-white truncate">{p.title}</div>
                        <div className="text-[10px] text-white/60 truncate">{(p.platforms || []).join(' · ')} · {(p.scheduled_at || '').slice(0, 16)}</div>
                        {p.moderation_hits?.length > 0 && (
                          <div className="text-[10px] text-red-300 flex items-center gap-1 mt-0.5">
                            <AlertTriangle className="w-3 h-3" /> {p.moderation_hits.join(', ')}
                          </div>
                        )}
                      </div>
                      <div className="flex gap-1 shrink-0">
                        {p.status !== 'published' && (
                          <button onClick={() => updateStatus(p.id, 'published')} className="p-1.5 rounded-full bg-green-500/20 hover:bg-green-500/30" title="Mark published" data-testid={`sa-publish-${p.id}`}>
                            <Check className="w-3 h-3 text-green-300" />
                          </button>
                        )}
                        {p.status !== 'rejected' && (
                          <button onClick={() => updateStatus(p.id, 'rejected')} className="p-1.5 rounded-full bg-yellow-500/20 hover:bg-yellow-500/30" title="Reject" data-testid={`sa-reject-${p.id}`}>
                            <X className="w-3 h-3 text-yellow-300" />
                          </button>
                        )}
                        <button onClick={() => del(p.id)} className="p-1.5 rounded-full bg-red-500/20 hover:bg-red-500/30" data-testid={`sa-del-${p.id}`}>
                          <Trash2 className="w-3 h-3 text-red-300" />
                        </button>
                      </div>
                    </div>
                  </div>
                ))
              }
            </div>
          )}

          {tab === 'mod' && (
            <div data-testid="sa-mod">
              <div className="flex items-center gap-2 mb-2">
                <Shield className="w-3.5 h-3.5 text-indigo-300" />
                <div className="text-xs font-semibold text-indigo-200">{at('saModTitle')}</div>
              </div>
              <p className="text-[10px] text-white/60 mb-2">{at('saModDesc')}</p>
              <div className="flex gap-1.5 mb-2">
                <input value={newTerm} onChange={e => setNewTerm(e.target.value)} onKeyDown={e => e.key === 'Enter' && addTerm()} placeholder={at('saModAdd')} className="flex-1 bg-black/40 border border-indigo-500/30 rounded-full px-3 py-1.5 text-xs text-white placeholder-white/40 focus:outline-none focus:border-indigo-400" data-testid="sa-mod-input" />
                <button onClick={addTerm} className="px-3 rounded-full bg-indigo-500 hover:bg-indigo-600 text-white text-xs font-semibold flex items-center gap-1" data-testid="sa-mod-add"><Plus className="w-3 h-3" /></button>
              </div>
              <div className="flex flex-wrap gap-1 mb-3 max-h-40 overflow-y-auto">
                {forbidden.map(t => (
                  <span key={t} className="px-2 py-1 rounded-full bg-red-500/20 border border-red-500/40 text-[10px] text-red-200 flex items-center gap-1">
                    {t}
                    <button onClick={() => removeTerm(t)} className="hover:text-white" data-testid={`sa-mod-remove-${t}`}><X className="w-3 h-3" /></button>
                  </span>
                ))}
              </div>
              <button onClick={saveMod} className="w-full py-2 rounded-full bg-gradient-to-r from-indigo-500 to-purple-600 text-white font-semibold text-xs" data-testid="sa-mod-save">{at('saModSave')}</button>
            </div>
          )}
        </>
      )}
    </div>
  );
}
